<template>
  <Teleport to="body">
    <Transition name="modal-backdrop">
      <div
        v-if="show"
        class="ui-modal__backdrop"
        :class="{
          'ui-modal__backdrop--blur': blurBackdrop,
          'ui-modal__backdrop--transparent': transparentBackdrop,
        }"
        :style="[
          backdropOpacity != null
            ? { backgroundColor: `rgba(0, 0, 0, ${backdropOpacity})` }
            : undefined,
          zIndex != null ? { zIndex: String(zIndex) } : undefined,
        ]"
        @click="handleBackdropClick"
      >
        <Transition name="modal">
          <div
            v-if="show"
            class="ui-modal__dialog"
            :class="[
              `ui-modal__dialog--${size}`,
              { 'ui-modal__dialog--centered': centered && !dialogStyle },
            ]"
            :style="[
              dialogMaxWidth ? { maxWidth: dialogMaxWidth } : undefined,
              dialogStyle || undefined,
              zIndex != null ? { zIndex: String(zIndex + 1) } : undefined,
            ]"
            @click.stop
            role="dialog"
            aria-modal="true"
            :aria-labelledby="title ? 'modal-title' : undefined"
          >
            <div
              class="ui-modal__content"
              :class="{ 'ui-modal__content--blue-header': headerVariant === 'blue' }"
              :style="contentMaxHeight ? { maxHeight: contentMaxHeight } : undefined"
            >
              <!-- Header -->
              <div
                v-if="title || $slots.header"
                class="ui-modal__header"
                :class="{
                  'ui-modal__header--blue': headerVariant === 'blue',
                  'ui-modal__header--title-centered': titleCentered,
                }"
              >
                <slot name="header">
                  <h2 v-if="title" id="modal-title" class="ui-modal__title">{{ title }}</h2>
                </slot>
                <button
                  v-if="showCloseButton"
                  type="button"
                  class="ui-modal__close"
                  @click="handleClose"
                  aria-label="Close modal"
                >
                  <Icon icon="mdi:close" />
                </button>
              </div>

              <!-- Body -->
              <div class="ui-modal__body" :class="bodyClass">
                <slot name="body"></slot>
                <slot></slot>
              </div>

              <!-- Footer -->
              <div v-if="$slots.footer" class="ui-modal__footer">
                <slot name="footer"></slot>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
import { Icon } from "@iconify/vue";
import { onMounted, onUnmounted, watch } from "vue";
import type { Size } from "./types";

// Stacked modal counter — only restore scroll when all modals close
let openModalCount = 0;

interface Props {
  show: boolean;
  title?: string;
  size?: Size | "xxs" | "xl" | "xxl" | "full";
  dialogMaxWidth?: string;
  dialogStyle?: Record<string, string>;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  blurBackdrop?: boolean;
  transparentBackdrop?: boolean;
  backdropOpacity?: number;
  centered?: boolean;
  headerVariant?: "default" | "blue";
  titleCentered?: boolean;
  contentMaxHeight?: string;
  zIndex?: number;
  bodyClass?: string | string[] | Record<string, boolean>;
}

const props = withDefaults(defineProps<Props>(), {
  size: "md",
  closeOnBackdrop: true,
  closeOnEscape: true,
  showCloseButton: true,
  blurBackdrop: true,
  transparentBackdrop: false,
  backdropOpacity: undefined,
  centered: true,
  headerVariant: "default",
  titleCentered: false,
});

const emit = defineEmits<{
  close: [];
  opened: [];
  closed: [];
}>();

const handleClose = () => {
  emit("close");
};

const handleBackdropClick = () => {
  if (props.closeOnBackdrop) {
    handleClose();
  }
};

const handleEscape = (event: KeyboardEvent) => {
  if (event.key === "Escape" && props.closeOnEscape && props.show) {
    handleClose();
  }
};

watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      openModalCount++;
      emit("opened");
      if (!props.transparentBackdrop) {
        document.body.style.overflow = "hidden";
      }
    } else {
      openModalCount = Math.max(0, openModalCount - 1);
      emit("closed");
      if (!props.transparentBackdrop && openModalCount === 0) {
        document.body.style.overflow = "";
      }
    }
  }
);

onMounted(() => {
  document.addEventListener("keydown", handleEscape);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleEscape);
  if (props.show) {
    openModalCount = Math.max(0, openModalCount - 1);
  }
  if (!props.transparentBackdrop && openModalCount === 0) {
    document.body.style.overflow = "";
  }
});
</script>

<style scoped>
.ui-modal__backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
}

.ui-modal__backdrop--blur {
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}

.ui-modal__backdrop--transparent {
  background-color: transparent;
}

.ui-modal__dialog {
  position: relative;
  width: 100%;
  max-width: 100%;
  margin: auto;
  z-index: 10051;
  outline: none;
}

.ui-modal__dialog--centered {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100% - 2rem);
}

.ui-modal__dialog--xxs {
  max-width: 260px;
}

.ui-modal__dialog--xs {
  max-width: 380px;
}

.ui-modal__dialog--sm {
  max-width: 480px;
}

.ui-modal__dialog--md {
  max-width: 640px;
}

.ui-modal__dialog--lg {
  max-width: 800px;
}

.ui-modal__dialog--xl {
  max-width: 1024px;
}

.ui-modal__dialog--xxl {
  max-width: 1400px;
}

.ui-modal__dialog--full {
  max-width: calc(100vw - 2rem);
}

.ui-modal__dialog--full .ui-modal__content {
  background: transparent;
  box-shadow: none;
  overflow: visible;
}

.ui-modal__dialog--full .ui-modal__body {
  overflow: hidden;
}

.ui-modal__content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: var(--color-bg-white);
  border-radius: 12px;
  box-shadow: 0 16px 70px -12px rgba(0, 0, 0, 0.25);
  max-height: 92vh;
  overflow: hidden;
  outline: none;
}

/* Header */
.ui-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border-light);
  flex-shrink: 0;
}

.ui-modal__header--blue {
  background: linear-gradient(90deg, #2d3bff 0%, #4f63ff 100%);
  border-bottom: none;
}

.ui-modal__header--blue .ui-modal__title {
  color: var(--color-bg-white);
  font-size: var(--font-size-md);
  font-weight: 500;
}

.ui-modal__header--blue .ui-modal__close {
  color: var(--color-bg-white);
}

.ui-modal__header--blue .ui-modal__close:hover {
  background-color: rgba(255, 255, 255, 0.15);
  color: var(--color-bg-white);
}

.ui-modal__header--blue .ui-modal__close .iconify {
  color: var(--color-bg-white);
}

.ui-modal__header--title-centered {
  justify-content: center;
  position: relative;
}

.ui-modal__header--title-centered .ui-modal__title {
  text-align: center;
}

.ui-modal__header--title-centered .ui-modal__close {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 0;
}

.ui-modal__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-dark);
  line-height: 1.5;
  letter-spacing: -0.01em;
}

/* Close button */
.ui-modal__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  margin-left: auto;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: var(--color-text-muted);
  opacity: 0.7;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.ui-modal__close:hover {
  background-color: var(--color-bg-muted);
  color: var(--color-text-secondary);
  opacity: 1;
}

.ui-modal__close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--color-primary-light, rgba(79, 70, 229, 0.3));
  opacity: 1;
}

.ui-modal__close .iconify {
  font-size: 16px;
  width: 16px;
  height: 16px;
}

/* Body */
.ui-modal__body {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

/* Footer */
.ui-modal__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
  padding: 14px 20px;
  border-top: 1px solid var(--color-border-light);
  flex-shrink: 0;
}

/* Transitions */
.modal-backdrop-enter-active,
.modal-backdrop-leave-active {
  transition: opacity var(--transition-normal);
}

.modal-backdrop-enter-from,
.modal-backdrop-leave-to {
  opacity: 0;
}

.modal-enter-active {
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-leave-active {
  transition: all 0.15s ease-out;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.97);
}

/* Tablet responsive */
@media (max-width: 1100px) {
  .ui-modal__dialog--lg {
    max-width: calc(100vw - 140px);
  }

  .ui-modal__dialog--xl {
    max-width: calc(100vw - 100px);
  }

  .ui-modal__dialog--xxl {
    max-width: calc(100vw - 80px);
  }
}

/* Mobile responsive */
@media (max-width: 640px) {
  .ui-modal__backdrop {
    padding: 0;
    align-items: flex-end;
  }

  .ui-modal__dialog {
    max-width: 100%;
    width: 100%;
    margin: 0;
  }

  .ui-modal__content {
    border-radius: 12px 12px 0 0;
    max-height: 90vh;
    border-bottom: none;
  }

  .ui-modal__header,
  .ui-modal__body,
  .ui-modal__footer {
    padding: 14px var(--space-4);
  }

  .modal-enter-from,
  .modal-leave-to {
    transform: translateY(100%);
    opacity: 1;
  }
}
</style>
